import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL, endpoint } from "../../api/endpoint";
import { apiHandler } from "../../api";
import { useToasts } from "react-toast-notifications";

export const useReconDetails = () => {
  const { addToast } = useToasts();
  const navigate = useNavigate();

  const { reconId } = useParams();
  const dispatch = useDispatch();
  const { authData, storeId } = useSelector((state) => state.loginSlice);
  const isMounted = useRef(true);

  const [transactionData, setTransactionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [activeOrderIndex, setActiveOrderIndex] = useState(0);
  const [activeMessageTab, setActiveMessageTab] = useState(0);
  const settlementStatusOptions = [
    "PENDING",
    "SETTLED",
    "TO BE INITIATED"
  ];

  const [showSendReconModal, setShowSendReconModal] = useState(false);
  const [isSubmittingSendRecon, setIsSubmittingSendRecon] = useState(false);
  const [sendReconForm, setSendReconForm] = useState({
    transaction_id: "",
    orders: [],
  });

  // State for the new Issue Modal
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [isSubmittingIssue, setIsSubmittingIssue] = useState(false);
  const [paymentId, setPaymentId] = useState("");
  const [toSubscriberId, setToSubscriberId] = useState("");
  const [toSubscriberUri, setToSubscriberUri] = useState("");

  const handleGoBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    isMounted.current = true;

    const fetchReconDetails = async () => {
      if (!reconId || !authData) return;

      setLoading(true);
      setError(null);

      try {
        const result = await apiHandler({
          url: endpoint.GET_RECONS_Detail + reconId,
          method: "GET",
          authToken: authData,
          dispatch,
          storeId,
        });

        const data = result?.data?.data;

        if (isMounted.current && data) {
          setTransactionData(data);
          setActiveOrderIndex(0);
          setActiveMessageTab(0);
        }
      } catch (err) {
        if (isMounted.current) {
          //console.error("Error fetching recon details:", err);
          setError("Failed to fetch recon details.");
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };

    fetchReconDetails();

    return () => {
      isMounted.current = false;
    };
  }, [reconId, authData, dispatch]);

  const handleLatestSettlement = useCallback(
    async (e) => {
      e.preventDefault();

      if (!transactionData) {
        addToast("Transaction data not loaded yet.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      const currentOrder = transactionData?.orders?.[activeOrderIndex];
      if (!currentOrder) {
        addToast("No order selected.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      const {
        _id,
        buyer_app_id,
        buyer_app_uri,
        seller_app_id,
        seller_app_uri,
      } = currentOrder;

      if (
        !_id ||
        !buyer_app_id ||
        !buyer_app_uri ||
        !seller_app_id ||
        !seller_app_uri
      ) {
        //console.error("Invalid or incomplete order details", currentOrder);
        addToast(
          "Cannot send settlement request. Missing order or app details.",
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        return;
      }

      try {
        setLoading(true);

        const url = `${endpoint.SEND_SETTLEMENT_REQUEST}`;

        const payload = {
          order_ids: [_id],
          app_details: {
            buyer_app_id,
            buyer_app_uri,
            seller_app_id,
            seller_app_uri,
          },
        };

        const response = await apiHandler({
          url,
          method: "POST",
          data: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
          },
          authToken: authData,
          dispatch,
          storeId,
        });

        const responseStatus = response?.data?.status || response?.status;
        const failedOrdersList =
          response?.data?.response?.data?.failedOrders?.filter(
            (item) => item && typeof item === "object" && item.orderId
          ) || [];

        let toastMessage =
          response?.data?.response?.data?.message ||
          "Settlement requested successfully";

        let failedOrdersContent = null;
        if (failedOrdersList.length > 0) {
          failedOrdersContent = (
            <div style={{ marginTop: "10px" }}>
              <strong>Failed Order:</strong>
              <ul style={{ marginTop: "5px", color: "#e74c3c" }}>
                {failedOrdersList.map((order, index) => (
                  <li key={index}>
                    <strong>Order ID:</strong> {order.orderId} <br />
                    <strong>Error:</strong> {order.error}
                  </li>
                ))}
              </ul>
            </div>
          );
        }

        addToast(
          <>
            <div>{toastMessage}</div>
            {failedOrdersContent}
          </>,
          {
            appearance: responseStatus === 200 ? "success" : "error",
            autoDismiss: true,
          }
        );
      } catch (error) {
        //console.error("API Error:", error);

        addToast(
          error.response?.data?.message || "Unexpected error occurred.",
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      } finally {
        setLoading(false);
      }
    },
    [authData, dispatch, transactionData, activeOrderIndex, addToast, storeId]
  );

  const handleRaiseRecon = useCallback(
    async (e) => {
      e.preventDefault();

      if (!transactionData) {
        addToast("Transaction data not loaded yet.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      const currentOrder = transactionData?.orders?.[activeOrderIndex];
      if (!currentOrder) {
        addToast("No order selected.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      const {
        _id,
        buyer_app_id,
        buyer_app_uri,
        seller_app_id,
        seller_app_uri,
      } = currentOrder;

      if (
        !_id ||
        !buyer_app_id ||
        !buyer_app_uri ||
        !seller_app_id ||
        !seller_app_uri
      ) {
        //console.error("Invalid or incomplete order details", currentOrder);
        addToast(
          "Cannot send settlement request. Missing order or app details.",
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        return;
      }

      try {
        setLoading(true);

        const url = `${endpoint.RAISE_RECON}`;

        const payload = {
          order_ids: [_id],
          app_details: {
            buyer_app_id,
            buyer_app_uri,
            seller_app_id,
            seller_app_uri,
          },
          ...(transactionData?.orders?.[activeOrderIndex]?.domain && { domain: transactionData.orders?.[activeOrderIndex]?.domain }),
        };
        const response = await apiHandler({
          url,
          method: "POST",
          data: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
          },
          authToken: authData,
          dispatch,
          storeId,
        });

        const responseStatus = response?.data?.status || response?.status;
        const failedOrdersList =
          response?.data?.response?.data?.failedOrders?.filter(
            (item) => item && typeof item === "object" && item.orderId
          ) || [];

        let toastMessage =
          response?.data?.response?.data?.message ||
          "Recon raised successfully";

        let failedOrdersContent = null;
        if (failedOrdersList.length > 0) {
          failedOrdersContent = (
            <div style={{ marginTop: "10px" }}>
              <strong>Failed Order:</strong>
              <ul style={{ marginTop: "5px", color: "#e74c3c" }}>
                {failedOrdersList.map((order, index) => (
                  <li key={index}>
                    <strong>Order ID:</strong> {order.orderId} <br />
                    <strong>Error:</strong> {order.error}
                  </li>
                ))}
              </ul>
            </div>
          );
        }

        addToast(
          <>
            <div>{toastMessage}</div>
            {failedOrdersContent}
          </>,
          {
            appearance: responseStatus === 200 ? "success" : "error",
            autoDismiss: true,
          }
        );
      } catch (error) {
        //console.error("API Error:", error);

        addToast(
          error.response?.data?.message || "Unexpected error occurred.",
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      } finally {
        setLoading(false);
      }
    },
    [authData, dispatch, transactionData, activeOrderIndex, addToast, storeId]
  );


  const handleOpenSendReconModal = useCallback(() => {
    const orders = Array.isArray(transactionData?.orders)
      ? transactionData.orders
      : [];

    setSendReconForm({
      transaction_id:
        transactionData?.recon_transaction_id ||
        transactionData?.transaction_id ||
        reconId ||
        "",
      orders: orders.map((order) => {
        const reconMessage = order?.messages?.find(
          (msg) => msg?.recon_sent?.settlements?.length
        );

        return {
          order_id: order?.bId || order?.b_id || order?.order_id || "",
          settlement_id:
            order?.settlement_id ||
            reconMessage?.recon_sent?.settlements?.[0]?.settlement_id ||
            "",
          recon_accord: Boolean(order?.recon_accord),
          settlement_status:
            order?.settlement_status ||
            order?.settlementStatus ||
            "PENDING",
          settlement_amount:
            order?.settlement_amount ??
            order?.settled_amount ??
            "",
          settlement_commission:
            order?.settlement_commission ??
            order?.taxes_charges_summary?.buyer_finder_fee ??
            "",
        };
      }),
    });

    setShowSendReconModal(true);
  }, [transactionData, reconId]);

  const handleCloseSendReconModal = useCallback(() => {
    setShowSendReconModal(false);
  }, []);

  const handleSendReconFieldChange = useCallback((field, value) => {
    setSendReconForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleSendReconOrderChange = useCallback((index, field, value) => {
    setSendReconForm((prev) => ({
      ...prev,
      orders: prev.orders.map((order, orderIndex) =>
        orderIndex === index ? { ...order, [field]: value } : order,
      ),
    }));
  }, []);

  const handleSendReconSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!sendReconForm.transaction_id?.trim()) {
        addToast("Transaction ID is required.", {
          appearance: "warning",
          autoDismiss: true,
        });
        return;
      }

      if (!Array.isArray(sendReconForm.orders) || !sendReconForm.orders.length) {
        addToast("No orders available for recon submission.", {
          appearance: "warning",
          autoDismiss: true,
        });
        return;
      }

      const invalidOrder = sendReconForm.orders.find((order) => {
        const amount = Number(order.settlement_amount);
        const commission = Number(order.settlement_commission);

        return (
          !order.order_id?.trim() ||
          !order.settlement_id?.trim() ||
          !order.settlement_status?.trim() ||
          !Number.isFinite(amount) ||
          amount <= 0 ||
          !Number.isFinite(commission) ||
          commission < 0
        );
      });

      if (invalidOrder) {
        addToast("Please complete all recon fields with valid values.", {
          appearance: "warning",
          autoDismiss: true,
        });
        return;
      }

      try {
        setIsSubmittingSendRecon(true);

        const payload = {
          transaction_id: sendReconForm.transaction_id.trim(),
          orders: sendReconForm.orders.map((order) => ({
            order_id: order.order_id.trim(),
            recon_accord: Boolean(order.recon_accord),
            settlement_id: order.settlement_id.trim(),
            settlement_status:
              order.settlement_status.trim() === "TO BE INITIATED"
                ? "TO_BE_INITIATED"
                : order.settlement_status.trim(),
            settlement_amount: String(Number(order.settlement_amount)),
            settlement_commission: String(Number(order.settlement_commission)),
          })),
        };

        const response = await apiHandler({
          url: endpoint.SEND_ON_RECON_REQUEST,
          method: "POST",
          data: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
            tenantid: storeId,
          },
          authToken: authData,
          dispatch,
          storeId,
        });

        const responseData = response?.data;
        const responseMessage =
          responseData?.message ||
          responseData?.data?.message ||
          "Recon submitted successfully.";

        addToast(responseMessage, {
          appearance:
            response?.status === 200 || responseData?.status === 200
              ? "success"
              : "error",
          autoDismiss: true,
        });

        if (response?.status === 200 || responseData?.status === 200) {
          setShowSendReconModal(false);
        }
      } catch (error) {
        addToast(
          error.response?.data?.message ||
          error.message ||
          "Failed to submit recon request.",
          {
            appearance: "error",
            autoDismiss: true,
          },
        );
      } finally {
        setIsSubmittingSendRecon(false);
      }
    },
    [addToast, authData, dispatch, sendReconForm, storeId],
  );

  const handleRaiseIssueSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!paymentId || !toSubscriberId || !toSubscriberUri) {
        addToast("All fields are required.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      // if (!storeId) {
      //   addToast("Store ID (Tenant ID) not available. Cannot raise issue.", {
      //     appearance: "error",
      //     autoDismiss: true,
      //   });
      //   return;
      // }

      setIsSubmittingIssue(true);

      try {
        const url = `${endpoint.RAISE_RECON_ISSUE}?`;

        const payload = {
          tenant_id: storeId,
          payment_id: paymentId,
          to_subscriber_id: toSubscriberId,
          to_subscriber_uri: toSubscriberUri,
          domain: transactionData?.orders?.[activeOrderIndex]?.domain, // Use domain from transaction data or fallback to "default"
        };

        const response = await apiHandler({
          url,
          method: "POST",
          data: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
          },
          authToken: authData,
          dispatch,
          storeId,
        });

        // --- MODIFIED SECTION ---
        const responseData = response?.data;
        //console.log("Raise Issue API Response:", responseData);

        if (responseData?.status === 200 && responseData?.redirectUrl) {
          // Success: We have a redirect URL.
          addToast("Issue raised. Redirecting to IGM...", {
            appearance: "success",
            autoDismiss: true,
          });

          // Open the redirect in a new tab
          window.open(responseData.redirectUrl, "_blank", "noopener,noreferrer");

          // Page is navigating away, no need to close modal or stop spinner
        } else {
          // Handle cases where the API call succeeded but didn't return
          // a redirectUrl, or returned a different status in its body.
          const toastMessage =
            responseData?.message ||
            "Issue raised successfully, but no redirect was provided.";

          addToast(toastMessage, {
            appearance: responseData?.status === 200 ? "success" : "error",
            autoDismiss: true,
          });

          if (responseData?.status === 200) {
            setShowIssueModal(false); // Close modal only on non-redirect success
          }
        }
        // --- END OF MODIFIED SECTION ---
      } catch (error) {
        //console.error("API Error raising issue:", error);

        // This catch block handles network errors or HTTP 4xx/5xx responses
        addToast(
          error.response?.data?.message ||
          "Unexpected error occurred while raising issue.",
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      } finally {
        // This will only be executed if no redirect happens
        setIsSubmittingIssue(false);
      }
    },
    [
      paymentId,
      toSubscriberId,
      toSubscriberUri,
      authData,
      dispatch,
      storeId,
      addToast,
    ]
  );

  // New handlers for opening/closing the modal
  const handleOpenIssueModal = useCallback(() => {
    const currentOrder = transactionData?.orders?.[activeOrderIndex];
    if (currentOrder) {
      // Pre-populate fields based on the current order
      setToSubscriberId(currentOrder.buyer_app_id || "");
      setToSubscriberUri(currentOrder.buyer_app_uri || "");
    }
    setPaymentId(""); // Clear payment ID for user input
    setShowIssueModal(true);
  }, [transactionData, activeOrderIndex]);

  const handleCloseIssueModal = () => {
    setShowIssueModal(false);
  };

  return {
    transactionData,
    loading,
    error,
    activeTab,
    activeOrderIndex,
    activeMessageTab,
    handleLatestSettlement,
    handleTabClick: setActiveTab,
    handleOrderClick: (index) => {
      setActiveOrderIndex(index);
      setActiveMessageTab(0);
    },
    handleMessageTabClick: setActiveMessageTab,
    handleRaiseRecon,


    showSendReconModal,
    handleOpenSendReconModal,
    handleCloseSendReconModal,
    handleSendReconFieldChange,
    handleSendReconOrderChange,
    handleSendReconSubmit,
    isSubmittingSendRecon,
    sendReconForm,
    settlementStatusOptions,

    // Export new modal properties
    showIssueModal,
    isSubmittingIssue,
    paymentId,
    setPaymentId,
    toSubscriberId,
    setToSubscriberId,
    toSubscriberUri,
    setToSubscriberUri,
    handleOpenIssueModal,
    handleCloseIssueModal,
    handleRaiseIssueSubmit,
    handleGoBack
  };
};
